import {
  type AppOperatorBridgeCommand,
  type AppOperatorBridgeExecuteResult,
  type AppOperatorBridgeTarget,
  type AppOperatorClientActionResult,
  type AppOperatorIframeMessage,
  type AppOperatorToolResultData,
  isRecord,
  normalizeAppOperatorRect,
} from './operator-contract';
import { getAIOperatorPresentationStore } from './operator-presentation';

const MESSAGE_TYPE = 'streamlain:app-operator-command/v1';
const RESPONSE_TYPE = 'streamlain:app-operator-response/v1';
const BRIDGE_TIMEOUT_MS = 8_000;
const OPERATOR_BRIDGE_TOKEN_PARAM = '__slOperatorBridgeToken';
const OPERATOR_BRIDGE_PARENT_ORIGIN_PARAM = '__slOperatorParentOrigin';

type AppOperatorIframeResponseMessage = Extract<
  AppOperatorIframeMessage,
  { type: 'streamlain:app-operator-response/v1' }
>;

export interface AppOperatorBridgeRegistration {
  customAppId: string;
  iframe: HTMLIFrameElement;
  operatorBridgeToken: string;
  allowedNavigationPrefix?: string;
  navigateTo?: (path: string) => void;
}

export interface ExecuteAppOperatorClientActionInput {
  data: AppOperatorToolResultData;
  clientInstanceId: string;
  acknowledge: (body: {
    success: boolean;
    clientInstanceId: string;
    error?: string;
    data?: Record<string, unknown>;
  }) => Promise<void>;
}

const registrations = new Map<string, AppOperatorBridgeRegistration>();
const ABSOLUTE_URL_PROTOCOL = /^[a-zA-Z][a-zA-Z\d+.-]*:/;

function nextRequestId(): string {
  return `app-operator-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function createAppOperatorBridgeToken(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `operator-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function appendAppOperatorBridgeToken(
  rawUrl: string,
  token: string,
  parentOrigin = window.location.origin
): string {
  const url = new URL(rawUrl, window.location.href);
  url.searchParams.set(OPERATOR_BRIDGE_TOKEN_PARAM, token);
  url.searchParams.set(OPERATOR_BRIDGE_PARENT_ORIGIN_PARAM, parentOrigin);
  return url.toString();
}

function iframeTargetOrigin(iframe: HTMLIFrameElement): string {
  const origin = new URL(iframe.src, window.location.href).origin;
  if (!origin || origin === 'null') {
    throw new Error('Custom app iframe origin is not addressable');
  }
  return origin;
}

function hasDotPathSegment(path: string): boolean {
  const [pathname] = path.split(/[?#]/, 1);
  return pathname.split('/').some(segment => {
    try {
      const decoded = decodeURIComponent(segment);
      return decoded === '.' || decoded === '..';
    } catch {
      return true;
    }
  });
}

function normalizeShellNavigationPrefix(prefix: string | undefined): string {
  const trimmed = prefix?.trim() ?? '';
  if (!trimmed) {
    throw new Error('Custom app shell navigation scope is not registered');
  }
  if (
    !trimmed.startsWith('/') ||
    trimmed.startsWith('//') ||
    ABSOLUTE_URL_PROTOCOL.test(trimmed) ||
    hasDotPathSegment(trimmed)
  ) {
    throw new Error('Custom app shell navigation scope is invalid');
  }
  const url = new URL(trimmed, window.location.origin);
  if (url.origin !== window.location.origin || url.search || url.hash) {
    throw new Error('Custom app shell navigation scope is invalid');
  }
  const normalized = url.pathname.replace(/\/+$/, '');
  if (!normalized || normalized === '/') {
    throw new Error('Custom app shell navigation scope is invalid');
  }
  return normalized;
}

function normalizeShellNavigationTarget(
  path: string,
  allowedPrefix: string | undefined
): string {
  const trimmed = path.trim();
  if (
    !trimmed ||
    !trimmed.startsWith('/') ||
    trimmed.startsWith('//') ||
    ABSOLUTE_URL_PROTOCOL.test(trimmed) ||
    hasDotPathSegment(trimmed)
  ) {
    throw new Error(
      'Custom app shell navigation target is outside the registered app'
    );
  }
  const prefix = normalizeShellNavigationPrefix(allowedPrefix);
  const url = new URL(trimmed, window.location.origin);
  const normalizedPath = url.pathname.replace(/\/+$/, '');
  if (
    url.origin !== window.location.origin ||
    (normalizedPath !== prefix && !normalizedPath.startsWith(`${prefix}/`))
  ) {
    throw new Error(
      'Custom app shell navigation target is outside the registered app'
    );
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

function mapRectToParent(
  iframe: HTMLIFrameElement,
  rect: unknown
): ReturnType<typeof normalizeAppOperatorRect> {
  const normalized = normalizeAppOperatorRect(rect);
  if (!normalized) return undefined;
  const frameRect = iframe.getBoundingClientRect();
  const rootRect = iframe.parentElement?.getBoundingClientRect();
  const offsetX = rootRect ? frameRect.left - rootRect.left : frameRect.left;
  const offsetY = rootRect ? frameRect.top - rootRect.top : frameRect.top;
  return {
    x: offsetX + normalized.x,
    y: offsetY + normalized.y,
    width: normalized.width,
    height: normalized.height,
  };
}

function mapTargetToParent(
  registration: AppOperatorBridgeRegistration,
  target: AppOperatorBridgeTarget | undefined
): AppOperatorBridgeTarget | undefined {
  if (!target) return undefined;
  return {
    ...target,
    rect: mapRectToParent(registration.iframe, target.rect),
    fieldRect: mapRectToParent(registration.iframe, target.fieldRect),
  };
}

function sendBridgeMessage(
  registration: AppOperatorBridgeRegistration,
  phase: 'describe' | 'execute',
  command: AppOperatorBridgeCommand
): Promise<AppOperatorIframeResponseMessage> {
  const targetWindow = registration.iframe.contentWindow;
  if (!targetWindow) {
    return Promise.reject(new Error('Custom app iframe is not ready'));
  }
  const requestId = nextRequestId();
  const message: AppOperatorIframeMessage = {
    type: MESSAGE_TYPE,
    requestId,
    appId: command.customAppId,
    operatorBridgeToken: registration.operatorBridgeToken,
    phase,
    command,
  };
  const targetOrigin = iframeTargetOrigin(registration.iframe);

  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Custom app operator bridge timed out'));
    }, BRIDGE_TIMEOUT_MS);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== targetOrigin) return;
      if (event.source !== targetWindow) return;
      const response = event.data;
      if (
        !isRecord(response) ||
        response.type !== RESPONSE_TYPE ||
        response.requestId !== requestId ||
        response.appId !== command.customAppId
      ) {
        return;
      }
      window.clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      if (response.success === false) {
        reject(
          new Error(
            typeof response.error === 'string'
              ? response.error
              : 'Custom app operator bridge failed'
          )
        );
        return;
      }
      resolve(response as AppOperatorIframeResponseMessage);
    };

    window.addEventListener('message', onMessage);
    targetWindow.postMessage(message, targetOrigin);
  });
}

export function registerAppOperatorBridge(
  registration: AppOperatorBridgeRegistration
): () => void {
  registrations.set(registration.customAppId, registration);
  return () => {
    const current = registrations.get(registration.customAppId);
    if (current === registration) {
      registrations.delete(registration.customAppId);
    }
  };
}

export function getRegisteredAppOperatorBridge(
  customAppId: string
): AppOperatorBridgeRegistration | undefined {
  return registrations.get(customAppId);
}

async function executeBrowserBridge(
  registration: AppOperatorBridgeRegistration,
  data: AppOperatorToolResultData
): Promise<AppOperatorBridgeExecuteResult | undefined> {
  const bridgeCommand = data.bridgeCommand;
  if (!bridgeCommand) {
    throw new Error('Custom app command has no browser bridge command');
  }
  const command: AppOperatorBridgeCommand = {
    customAppId: data.customAppId,
    controlId: bridgeCommand.controlId,
    payload: bridgeCommand.payload,
  };
  const store = getAIOperatorPresentationStore(data.customAppId);
  const described = await sendBridgeMessage(registration, 'describe', command);
  const describedTarget = described.success
    ? mapTargetToParent(registration, described.target)
    : undefined;
  await store.presentTarget(describedTarget);
  const executed = await sendBridgeMessage(registration, 'execute', command);
  if (!executed.success || !executed.result?.success) {
    throw new Error('Custom app operator bridge returned an invalid result');
  }
  const bridgeResult = {
    ...executed.result,
    target: mapTargetToParent(registration, executed.result.target),
  };
  await store.presentExecutionResult(bridgeResult?.target ?? describedTarget);
  return bridgeResult;
}

export async function executeAppOperatorClientAction(
  input: ExecuteAppOperatorClientActionInput
): Promise<void> {
  const { data, clientInstanceId } = input;
  try {
    const registration = registrations.get(data.customAppId);
    if (!registration) {
      throw new Error('Custom app is not open in this browser tab');
    }

    let bridgeResult: AppOperatorBridgeExecuteResult | undefined;
    let shellNavigateTo: string | undefined;
    if (data.executionMode === 'browser_bridge') {
      bridgeResult = await executeBrowserBridge(registration, data);
    } else if (data.executionMode === 'workspace_shell') {
      if (!data.navigateTo) {
        throw new Error('Custom app shell command has no navigation target');
      }
      if (!registration.navigateTo) {
        throw new Error('Custom app shell navigation is not available');
      }
      shellNavigateTo = normalizeShellNavigationTarget(
        data.navigateTo,
        registration.allowedNavigationPrefix
      );
      registration.navigateTo(shellNavigateTo);
    } else {
      throw new Error('Custom app command is not a client-side action');
    }

    const ackData: AppOperatorClientActionResult = {
      docId: data.targetDocId,
      customAppId: data.customAppId,
      controlId: data.controlId,
      executionMode: data.executionMode,
      ...(shellNavigateTo ? { navigateTo: shellNavigateTo } : {}),
      ...(bridgeResult ? { bridgeResult } : {}),
    };
    await input.acknowledge({
      success: true,
      clientInstanceId,
      data: ackData as unknown as Record<string, unknown>,
    });
  } catch (error) {
    await input.acknowledge({
      success: false,
      clientInstanceId,
      error: error instanceof Error ? error.message : String(error),
      data: { docId: data.targetDocId, customAppId: data.customAppId },
    });
    throw error;
  }
}
